// =========================
// UTILS.JS
// =========================

// External Libraries
import gsap from 'gsap';

// App Modules
import App from './App';

// This is the Utils class that contains various utility functions
// used throughout the application. It includes methods for formatting field names,
// getting supplier details, formatting timestamps, and more.
class Utils {
  // Function to animate the page intro
  pageIntroAnim() {
    const pageContent = document.querySelector('.page-content');
    if (!pageContent) return;
    gsap.fromTo(
      pageContent,
      { opacity: 0, y: -12 },
      { opacity: 1, y: 0, ease: 'power2.out', duration: 0.3 }
    );
  }

  // Function to format a field name from camelCase to human-readable format
  formatFieldName(field) {
    return field
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, (str) => str.toUpperCase());
  }

  // Function to get supplier details by user ID
  getSupplierDetails(userId) {
    return fetch(`${App.apiBase}/supplier/byUser/${userId}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
      },
    })
      .then((res) => (res.ok ? res.json() : null))
      .catch((err) => {
        console.warn('Failed to fetch supplier details:', err);
        return null;
      });
  }

  // Function to get user display name based on role
  getDisplayName(user) {
    if (!user) return 'User';

    if (user.role === 'supplier') {
      if (user.businessName) {
        return user.businessName;
      } else {
        return 'Business User';
      }
    } else {
      const fullName = `${user.firstName || ''} ${user.lastName || ''}`.trim();
      return fullName || 'User';
    }
  }

  // Function to get user profile image URL
  // If the user is a supplier, return the business profile image
  getProfileImage(user) {
    if (!user) return `${App.apiBase}/static/default-profile.png`;

    const isSupplier = user.role === 'supplier';

    if (user.profileImage) {
      return user.profileImage.startsWith('http')
        ? user.profileImage
        : `${App.apiBase}/uploads/${user.profileImage}`;
    }

    return isSupplier
      ? `${App.apiBase}/static/default-profile-business.png`
      : `${App.apiBase}/static/default-profile.png`;
  }

  // Function to format a timestamp into a human-readable string
  formatTime(timestamp) {
    const created = new Date(timestamp);
    const now = new Date();
    const diff = now - created;
    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (seconds < 60) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;

    const options = { month: 'short', day: 'numeric' };
    if (created.getFullYear() !== now.getFullYear()) options.year = 'numeric';
    return created.toLocaleDateString(undefined, options);
  }
}

export default new Utils();
